/**
 * Sentry Test Button Component
 * 
 * This component provides buttons to test various Sentry features:
 * - Error tracking
 * - Custom messages
 * - User feedback
 * - Performance monitoring
 * 
 * Use this for development and testing purposes only.
 * Remove or hide in production.
 */

import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { captureException, captureMessage, addBreadcrumb, setTag, setContext } from '../config/sentry.config';
import { AlertTriangle, Bug, MessageSquare, Zap, Info } from 'lucide-react';

const SentryTestButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Only show in development mode
  if (!isDevelopment) {
    return null;
  }

  const testError = () => {
    try {
      // Send a log before throwing the error
      captureMessage('User triggered test error', 'info');
      
      // Add breadcrumb
      addBreadcrumb({
        category: 'test',
        message: 'Test error button clicked',
        level: 'info',
        data: {
          action: 'test_error_button_click',
          timestamp: new Date().toISOString(),
        },
      });

      // Throw an intentional error
      throw new Error('This is your first Sentry error! 🎉 - Everything is working correctly.');
    } catch (error) {
      captureException(error, {
        tags: {
          testType: 'manual',
          component: 'SentryTestButton',
        },
        extra: {
          testDescription: 'Intentional test error to verify Sentry integration',
        },
      });
    }
  };

  const testMessage = () => {
    captureMessage('Test message from Sentry Test Button', 'info');
    alert('Test message sent to Sentry!');
  };

  const testWarning = () => {
    captureMessage('Test warning message', 'warning');
    alert('Test warning sent to Sentry!');
  };

  const testBreadcrumbs = () => {
    // Add multiple breadcrumbs
    addBreadcrumb({
      category: 'navigation',
      message: 'User navigated to test page',
      level: 'info',
    });

    addBreadcrumb({
      category: 'user',
      message: 'User clicked test button',
      level: 'info',
      data: {
        buttonId: 'test-breadcrumbs',
        timestamp: new Date().toISOString(),
      },
    });

    addBreadcrumb({
      category: 'api',
      message: 'Simulated API call',
      level: 'info',
      data: {
        url: '/api/test',
        method: 'GET',
        status: 200,
      },
    });

    captureMessage('Test message with breadcrumbs trail', 'info');
    alert('Test breadcrumbs added and sent to Sentry!');
  };

  const testPerformance = () => {
    // Performance monitoring is automatically enabled via browserTracingIntegration
    // This test demonstrates manual performance tracking
    const startTime = performance.now();
    
    // Simulate some work
    setTimeout(() => {
      const duration = performance.now() - startTime;
      
      // Log performance as a breadcrumb
      addBreadcrumb({
        category: 'performance',
        message: 'Test performance operation completed',
        level: 'info',
        data: {
          duration: `${duration.toFixed(2)}ms`,
          operation: 'test-operation',
        },
      });
      
      captureMessage(`Performance test completed in ${duration.toFixed(2)}ms`, 'info');
      alert('Performance test completed and sent to Sentry!');
    }, 1000);
  };

  const testCustomContext = () => {
    // Set custom tags
    setTag('test_type', 'custom_context');
    setTag('test_timestamp', new Date().toISOString());

    // Set custom context
    setContext('test_context', {
      feature: 'Sentry Test Button',
      action: 'Testing custom context',
      customData: {
        foo: 'bar',
        nested: {
          value: 42,
        },
      },
    });

    captureMessage('Test message with custom context and tags', 'info');
    alert('Custom context and tags sent to Sentry!');
  };

  const testUnhandledPromiseRejection = () => {
    // Trigger an unhandled promise rejection
    Promise.reject(new Error('Test unhandled promise rejection - This should be caught by Sentry'));
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-full shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
        title="Open Sentry Test Panel"
      >
        <Bug className="w-5 h-5" />
        <span className="font-semibold">Test Sentry</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-6 w-96 max-h-[600px] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bug className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Sentry Test Panel
          </h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ✕
        </button>
      </div>

      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-30 rounded-lg border border-blue-200 dark:border-blue-700">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold mb-1">
              Development Mode Only
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300">
              This panel helps you test Sentry integration. Check your Sentry dashboard after triggering tests.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={testError}
          className="w-full flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <AlertTriangle className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Error</div>
            <div className="text-xs opacity-90">Throw and capture an error</div>
          </div>
        </button>

        <button
          onClick={testMessage}
          className="w-full flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <MessageSquare className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Message</div>
            <div className="text-xs opacity-90">Send an info message</div>
          </div>
        </button>

        <button
          onClick={testWarning}
          className="w-full flex items-center gap-3 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <AlertTriangle className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Warning</div>
            <div className="text-xs opacity-90">Send a warning message</div>
          </div>
        </button>

        <button
          onClick={testBreadcrumbs}
          className="w-full flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <Info className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Breadcrumbs</div>
            <div className="text-xs opacity-90">Add multiple breadcrumbs</div>
          </div>
        </button>

        <button
          onClick={testPerformance}
          className="w-full flex items-center gap-3 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <Zap className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Performance</div>
            <div className="text-xs opacity-90">Send performance transaction</div>
          </div>
        </button>

        <button
          onClick={testCustomContext}
          className="w-full flex items-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <Bug className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Custom Context</div>
            <div className="text-xs opacity-90">Add tags and context</div>
          </div>
        </button>

        <button
          onClick={testUnhandledPromiseRejection}
          className="w-full flex items-center gap-3 bg-pink-600 hover:bg-pink-700 text-white px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <AlertTriangle className="w-5 h-5" />
          <div className="text-left">
            <div className="font-semibold">Test Promise Rejection</div>
            <div className="text-xs opacity-90">Unhandled promise rejection</div>
          </div>
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <a
          href="https://o4510144287735808.ingest.us.sentry.io/issues/"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold"
        >
          View Sentry Dashboard →
        </a>
      </div>
    </div>
  );
};

export default SentryTestButton;

